import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import StatusBadge from '../../components/common/StatusBadge';
import Pill from '../../components/common/Pill';
import CopiarTexto from '../../components/common/CopiarTexto';
import ConfirmarModal from '../../components/common/ConfirmarModal';
import EndpointForm from './EndpointForm';
import usePaginacao from '../../hooks/usePaginacao';
import useDebounce from '../../hooks/useDebounce';
import { useAuth } from '../../contexts/AuthContext';
import * as endpointsApi from '../../api/endpoints';
import * as cadastrosApi from '../../api/cadastros';
import { formatDataHora, tempoRelativo } from '../../utils/date';
import { labelAuthTipo } from '../../utils/webhookOpcoes';

export default function Endpoints() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [endpoints, setEndpoints] = useState(null);
  const [busca, setBusca] = useState('');
  const buscaDebounced = useDebounce(busca);
  const [erro, setErro] = useState('');
  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [segredoVisivel, setSegredoVisivel] = useState({});
  const [clientes, setClientes] = useState([]);
  const [sistemas, setSistemas] = useState([]);

  const podeEditar = user?.perfil === 'admin' || user?.perfil === 'operador';

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const { data } = await endpointsApi.listar(buscaDebounced ? { busca: buscaDebounced } : {});
      setEndpoints(data);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar os endpoints.'));
    }
  }, [buscaDebounced]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    // alimenta os comboboxes do formulário; só os ativos entram como sugestão
    Promise.all([cadastrosApi.listar('cliente', true), cadastrosApi.listar('sistema', true)])
      .then(([resClientes, resSistemas]) => {
        setClientes(resClientes.data.map((c) => c.nome));
        setSistemas(resSistemas.data.map((s) => s.nome));
      })
      .catch(() => {
        /* sem as sugestões o campo continua aceitando texto livre */
      });
  }, []);

  const { paginados, Paginacao } = usePaginacao(endpoints || [], 12);

  if (erro && !endpoints) return <ErroCarregamento mensagem={erro} onRetry={carregar} />;
  if (!endpoints) return <LoadingSpinner />;

  const salvar = async (dados) => {
    if (editando) {
      await endpointsApi.atualizar(editando.id_endpoint, dados);
    } else {
      await endpointsApi.criar(dados);
    }
    await carregar();
    // o combobox pode ter criado cliente/sistema novo — as sugestões precisam saber
    const [resClientes, resSistemas] = await Promise.all([
      cadastrosApi.listar('cliente', true),
      cadastrosApi.listar('sistema', true),
    ]);
    setClientes(resClientes.data.map((c) => c.nome));
    setSistemas(resSistemas.data.map((x) => x.nome));
  };

  /** URL pronta para colar no painel do provedor, já com a credencial embutida.
   *  Só existe para os modos em que a credencial cabe na URL: token (query string) e Basic
   *  (usuário:senha@host). No HMAC a assinatura é calculada a cada requisição sobre o corpo —
   *  não há o que embutir. Devolve null quando não se aplica, e aí o botão não é renderizado. */
  const urlComCredencial = (e) => {
    if (e.auth_tipo === 'token' && e.token) {
      return `${e.url_publica}?token=${encodeURIComponent(e.token)}`;
    }
    if (e.auth_tipo === 'basic' && e.usuario_basic && e.senha_basic) {
      const credencial = `${encodeURIComponent(e.usuario_basic)}:${encodeURIComponent(e.senha_basic)}`;
      return e.url_publica.replace('://', `://${credencial}@`);
    }
    return null;
  };

  const credencialDe = (e) => {
    if (e.auth_tipo === 'token') return e.token;
    if (e.auth_tipo === 'hmac') return e.hmac_secret;
    if (e.auth_tipo === 'basic') return e.senha_basic;
    return null;
  };

  return (
    <div>
      <PageHeader
        titulo="Endpoints"
        subtitulo={`${endpoints.length} caixa(s) de entrada de webhook`}
        acoes={
          podeEditar && (
            <button
              type="button"
              className="wh-btn wh-btn-primary"
              onClick={() => {
                setEditando(null);
                setFormAberto(true);
              }}
            >
              <i className="bi bi-plus-lg" /> Novo endpoint
            </button>
          )
        }
      />

      <div className="card-soft mb-3">
        <div className="card-soft__body py-2">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-search" style={{ color: 'var(--wh-muted)' }} />
            <input
              className="wh-input border-0"
              placeholder="Buscar por nome, slug ou sistema..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              style={{ boxShadow: 'none' }}
            />
          </div>
        </div>
      </div>

      <div className="card-soft">
        {endpoints.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-hdd-network" />
            <div style={{ fontWeight: 600, color: 'var(--wh-ink)' }}>Nenhum endpoint cadastrado.</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              Você também pode simplesmente apontar o provedor para /hooks/&lt;qualquer-slug&gt; — o endpoint é criado
              na primeira chamada.
            </div>
          </div>
        ) : (
          <div className="wh-table-wrap">
            <table className="wh-table">
              <thead>
                <tr>
                  <th>Endpoint</th>
                  <th>URL pública</th>
                  <th>Autenticação</th>
                  <th>Situação</th>
                  <th style={{ textAlign: 'right' }}>Eventos</th>
                  <th>Último</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {paginados.map((e) => (
                  <tr key={e.id_endpoint}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{e.nome}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--wh-muted)' }}>
                        {[e.cliente, e.sistema].filter(Boolean).join(' · ') || 'sem cliente/sistema'}
                        {e.criado_automaticamente && (
                          <>
                            {' · '}
                            <span title="Nasceu de uma chamada em um slug desconhecido">criado automaticamente</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td style={{ minWidth: 260 }}>
                      <div className="url-box">
                        <code className="wh-mono">{e.url_publica}</code>
                        <CopiarTexto
                          texto={e.url_publica}
                          rotulo=""
                          titulo="Copiar a URL"
                          classe="wh-btn wh-btn-outline wh-btn-sm"
                        />
                        {podeEditar && urlComCredencial(e) && (
                          <CopiarTexto
                            texto={urlComCredencial(e)}
                            rotulo=""
                            icone="bi-key"
                            titulo={
                              e.auth_tipo === 'token'
                                ? 'Copiar a URL com o token na query — para provedor que só aceita configurar a URL. Contém o segredo: não cole em chamado nem em chat.'
                                : 'Copiar a URL com usuário e senha embutidos. Contém o segredo: não cole em chamado nem em chat.'
                            }
                            classe="wh-btn wh-btn-outline wh-btn-sm"
                          />
                        )}
                      </div>
                      {credencialDe(e) && (
                        <div className="d-flex align-items-center gap-1 mt-1">
                          <span style={{ fontSize: 11.5, color: 'var(--wh-muted)' }}>credencial:</span>
                          <code className="wh-mono" style={{ fontSize: 11 }}>
                            {segredoVisivel[e.id_endpoint] ? credencialDe(e) : '••••••••••••'}
                          </code>
                          <button
                            type="button"
                            className="wh-btn wh-btn-outline wh-btn-sm"
                            onClick={() =>
                              setSegredoVisivel((atual) => ({ ...atual, [e.id_endpoint]: !atual[e.id_endpoint] }))
                            }
                            title={segredoVisivel[e.id_endpoint] ? 'Ocultar' : 'Mostrar'}
                          >
                            <i className={`bi ${segredoVisivel[e.id_endpoint] ? 'bi-eye-slash' : 'bi-eye'}`} />
                          </button>
                          {segredoVisivel[e.id_endpoint] && <CopiarTexto texto={credencialDe(e)} rotulo="" />}
                        </div>
                      )}
                    </td>
                    <td>
                      <Pill tone={e.auth_tipo === 'nenhum' ? 'neutral' : 'accent'}>{labelAuthTipo(e.auth_tipo)}</Pill>
                      {e.rejeitar_invalido && (
                        <div style={{ fontSize: 11, color: 'var(--wh-muted)', marginTop: 2 }}>rejeita inválido</div>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={e.ativo ? 'ativo' : 'inativo'} />
                      {e.encaminhar_ativo && (
                        <div style={{ fontSize: 11, color: 'var(--wh-muted)', marginTop: 2 }} title={e.encaminhar_url}>
                          encaminhando
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{e.total_eventos}</td>
                    <td title={formatDataHora(e.ultimo_evento_em)} style={{ whiteSpace: 'nowrap' }}>
                      {e.ultimo_evento_em ? tempoRelativo(e.ultimo_evento_em) : '—'}
                    </td>
                    <td>
                      <div className="d-flex gap-1 justify-content-end">
                        <button
                          type="button"
                          className="wh-btn wh-btn-outline wh-btn-sm"
                          title="Ver eventos deste endpoint"
                          onClick={() => navigate(`/eventos?id_endpoint=${e.id_endpoint}`)}
                        >
                          <i className="bi bi-inbox" />
                        </button>
                        {podeEditar && (
                          <>
                            <button
                              type="button"
                              className="wh-btn wh-btn-outline wh-btn-sm"
                              title="Editar"
                              onClick={() => {
                                setEditando(e);
                                setFormAberto(true);
                              }}
                            >
                              <i className="bi bi-pencil" />
                            </button>
                            {(e.auth_tipo === 'token' || e.auth_tipo === 'hmac') && (
                              <button
                                type="button"
                                className="wh-btn wh-btn-outline wh-btn-sm"
                                title="Rotacionar credencial"
                                onClick={() =>
                                  setConfirmando({
                                    titulo: 'Rotacionar credencial',
                                    mensagem: `A credencial atual de "${e.nome}" deixa de valer imediatamente. As chamadas que continuarem usando a antiga serão registradas como credencial inválida até o provedor ser atualizado.`,
                                    textoBotao: 'Rotacionar',
                                    acao: () => endpointsApi.rotacionarCredencial(e.id_endpoint).then(carregar),
                                  })
                                }
                              >
                                <i className="bi bi-arrow-repeat" />
                              </button>
                            )}
                          </>
                        )}
                        {user?.perfil === 'admin' && (
                          <button
                            type="button"
                            className="wh-btn wh-btn-danger wh-btn-sm"
                            title="Excluir"
                            onClick={() =>
                              setConfirmando({
                                titulo: 'Excluir endpoint',
                                mensagem: `Excluir "${e.nome}" apaga também os ${e.total_eventos} evento(s) já recebidos. Não há como desfazer.`,
                                textoBotao: 'Excluir',
                                acao: () => endpointsApi.excluir(e.id_endpoint).then(carregar),
                              })
                            }
                          >
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Paginacao />

      {formAberto && (
        <EndpointForm
          endpoint={editando}
          clientes={clientes}
          sistemas={sistemas}
          onClose={() => setFormAberto(false)}
          onSalvar={salvar}
        />
      )}
      {confirmando && (
        <ConfirmarModal
          titulo={confirmando.titulo}
          mensagem={confirmando.mensagem}
          textoBotao={confirmando.textoBotao}
          onConfirmar={confirmando.acao}
          onClose={() => setConfirmando(null)}
        />
      )}
    </div>
  );
}
